/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Buyer company and executive selection handlers for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsBuyerHandlers
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { resolveBuyerCompanySelection } from "../../logic/appointments/buyerCompanySelection.js";
import { filterRequirementsByExecutive } from "../../logic/appointments/appointmentCalendarParams.js";

/**
 * @param {Object} ctx - ViewAppointments context
 * @param {Object} fetching - Fetch helpers
 * @returns {Object} Buyer selection handlers
 */
const useViewAppointmentsBuyerHandlers = (ctx, fetching) => {
  const {
    navigate,
    eventData,
    participatingCompanies,
    participatingCompanyData,
    companyRequestIdRef,
    appointmentsPageRequestRef,
    setSelectedBuyerCompany,
    setSelectedBuyerExecutive,
    setSelectedRequirement,
    setBuyerExecutives,
    setRequirements,
    setParticipatingCompanyData,
    setCurrentPage,
    setIsLoading,
  } = ctx;

  const { clearAppointmentViews } = fetching;

  const handleBuyerCompanyChange = useCallback(
    async (selectedOption) => {
      companyRequestIdRef.current += 1;
      const requestId = companyRequestIdRef.current;
      setSelectedBuyerCompany(selectedOption);
      setSelectedBuyerExecutive(null);
      setSelectedRequirement(null);
      setBuyerExecutives([]);
      setCurrentPage(1);
      appointmentsPageRequestRef.current += 1;
      clearAppointmentViews();

      if (!selectedOption?.value) {
        setRequirements([]);
        setParticipatingCompanyData(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const selection = await resolveBuyerCompanySelection({
          eventId: eventData._id,
          companyId: selectedOption.value,
          participatingCompanies,
          navigate,
        });

        if (requestId !== companyRequestIdRef.current) {
          return;
        }

        setParticipatingCompanyData(selection.participatingCompanyData);
        setBuyerExecutives(selection.buyerExecutives);
        setRequirements([]);
      } catch (error) {
        logger.error("Error handling buyer company selection:", {
          err: sanitizeError(error),
        });
        setBuyerExecutives([]);
        setRequirements([]);
      } finally {
        if (requestId === companyRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      companyRequestIdRef,
      setSelectedBuyerCompany,
      setSelectedBuyerExecutive,
      setSelectedRequirement,
      setBuyerExecutives,
      setCurrentPage,
      appointmentsPageRequestRef,
      clearAppointmentViews,
      setRequirements,
      setParticipatingCompanyData,
      setIsLoading,
      eventData,
      participatingCompanies,
      navigate,
    ],
  );

  const handleBuyerExecutiveChange = useCallback(
    (selectedOption) => {
      setSelectedBuyerExecutive(selectedOption);
      setSelectedRequirement(null);
      setCurrentPage(1);

      if (!selectedOption?.value) {
        setRequirements([]);
        appointmentsPageRequestRef.current += 1;
        clearAppointmentViews();
        return;
      }

      setRequirements(
        filterRequirementsByExecutive(
          participatingCompanyData,
          String(selectedOption.value),
        ),
      );
    },
    [
      setSelectedBuyerExecutive,
      setSelectedRequirement,
      setCurrentPage,
      setRequirements,
      appointmentsPageRequestRef,
      clearAppointmentViews,
      participatingCompanyData,
    ],
  );

  return { handleBuyerCompanyChange, handleBuyerExecutiveChange };
};

export default useViewAppointmentsBuyerHandlers;
